<script setup lang="ts">
  import { computed } from 'vue'
  import SvgIcon from '@shared/components/svg-icon.vue'
  const props = defineProps<{
    value: string | boolean | null | unknown[]
    modelValue: string | boolean | null | unknown[]
    label?: string
    isChecked?: boolean
    isDisabled?: boolean
    id?: string
    errorMsgList?: string[]
    isFilled?: boolean
  }>()

  const arrayEquals = (a: unknown[], b: unknown): boolean => {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index])
  }

  const isActive = computed(() => {
    if (Array.isArray(props.modelValue)) {
      return arrayEquals(props.modelValue, props.value)
    }
    return props.isChecked || props.modelValue === props.value
  })

  const radioClass = computed(() => {
    if (isActive.value) {
      return 'radio__checked'
    } else if (props.isDisabled) {
      return 'radio_disabled'
    }
    return ''
  })

  const radioIconClass = computed(() => {
    return {
      radio__icon_active: isActive.value,
      radio__icon_disabled: props.isDisabled,
      radio__icon_error: props.errorMsgList,
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const formatBooleanValue = (value: string) => value === 'true'

  const updateValue = (event: Event) => {
    const ev = event.target as HTMLInputElement
    if (typeof props.value === 'boolean') {
      return emit('update:modelValue', formatBooleanValue(ev.value))
    }
    if (Array.isArray(props.value)) {
      return emit('update:modelValue', props.value)
    }
    return emit('update:modelValue', props.value)
  }
</script>

<template>
  <el-form-item class="tw-max-w-[180px]" :class="{ filled: props.isFilled }">
    <label
      class="radio tw-flex tw-items-center"
      :class="[radioClass, { 'tw-mr-12 tw-my-4': !props.isFilled && props.label }]"
      :for="id"
    >
      <slot name="pre-content" />
      <input
        :id="id"
        v-bind="$attrs"
        class="radio__input tw-hidden"
        type="radio"
        :value="value"
        :checked="isActive"
        :disabled="isDisabled"
        @click="updateValue"
      />
      <span
        v-if="!props.isFilled"
        class="radio__icon tw-flex tw-items-center tw-justify-center"
        :class="[
          radioIconClass,
          {
            'tw-mr-0 tw-ml-12': $slots['pre-content'],
          },
          {
            'tw-mr-16': props.label,
          },
        ]"
      >
        <svg-icon v-if="isActive" class="progress-bar__status-icon tw-fill-white" name="check" size="18" />
      </span>
      <span v-if="label" class="tw-text-body-3" :class="isActive ? 'tw-text-primary-100' : 'tw-text-grey-800'">
        {{ label }}
      </span>
    </label>
  </el-form-item>
</template>

<style lang="scss" scoped>
  .el-form-item {
    @apply tw-m-0;
  }
  .radio {
    // removed max-width, it was causing issues with the radio button being cut off
    // recheck if there is any issues with this
    @apply tw-shrink tw-grow tw-basis-2/4;
    &_disabled {
      @apply tw-opacity-75 tw-cursor-not-allowed;
    }
    &__icon {
      $active-icon: &;
      @apply tw-border tw-border-primary tw-rounded-full tw-w-[25px] tw-h-[25px] tw-cursor-pointer;
      &_active {
        @apply tw-bg-primary-100 tw-border-primary-100 tw-w-[25px] tw-h-[25px] tw-rounded-full;
      }
      &_disabled {
        @apply tw-cursor-not-allowed;
      }
      &_error {
        @apply tw-border-accent-500;

        &#{$active-icon} {
          @apply tw-bg-accent-500;
        }
      }
    }
  }

  .filled {
    .radio {
      @apply tw-cursor-pointer tw-mb-0 tw-basis-0 tw-flex tw-justify-center tw-py-12 tw-px-16 tw-bg-grey-200 tw-border-2 tw-border-secondary-100;

      span {
        @apply tw-text-caption-1 tw-text-primary-100;
      }

      &__checked {
        @apply tw-bg-secondary-150 tw-border-primary;
      }
    }
  }
</style>
